import type { ModelRef } from "vue";

export default ({
  options,
  modelValue,
  emit,
  open,
  toggleDropdown,
  multiple,
  creatable = false,
}: {
  options: Ref<{ label: string; value: any; handler?: null | (() => void); disabled?: boolean }[] | null>;
  modelValue: ModelRef<string | number | unknown[]>;
  emit?: any;
  open: Ref<boolean>;
  toggleDropdown: () => void;
  multiple?: boolean;
  // combobox mode: the typed search can be selected as a value of its own;
  // a function decides what the typed text becomes (null = cannot be created) and can title it with a heading
  creatable?: boolean | ((typed: string) => (DropdownOption & { heading?: string }) | null);
}) => {
  const searchElm = ref<HTMLElement | null>(null);
  const optionsWrapper = ref<HTMLElement | null>(null);

  const search = ref("");

  type ListOption = (DropdownOption & { isHeading?: boolean }) | null;

  // a list with headings is grouped: groups keep their order and a heading stays while its group has a match
  const filterGroups = (list: ListOption[], matches: (option: DropdownOption) => boolean) => {
    const groups: { heading: ListOption; items: ListOption[] }[] = [{ heading: null, items: [] }];
    for (const option of list) {
      if (!option) groups.push({ heading: null, items: [] });
      else if (option.isHeading) groups.push({ heading: option, items: [] });
      else groups.at(-1)!.items.push(option);
    }
    return groups
      .map((group) => ({ ...group, items: group.items.filter((option) => matches(option!)) }))
      .filter((group) => group.items.length)
      .flatMap((group, index) => [...(index ? [null] : []), ...(group.heading ? [group.heading] : []), ...group.items]);
  };

  // combobox: the option the typed search would create, null when nothing can be created or it already exists
  const createdOption = computed<(DropdownOption & { heading?: string }) | null>(() => {
    const typed = search.value.trim();
    if (!creatable || !typed || !options?.value) return null;
    const created = creatable === true ? { label: typed, value: typed } : creatable(typed);
    return created && !options.value.some((option) => option?.value === created.value) ? created : null;
  });

  // Enter in the search adds the typed value, unless it is not valid yet (disabled)
  const selectCreatedOption = () => {
    if (createdOption.value && !createdOption.value.disabled) selectOption(createdOption.value.value);
  };

  const filteredOptions = computed<{ label: string; value: any; image?: string | null }[]>(() => {
    if (!options?.value) return [];
    let filtered = options.value;
    const grouped = filtered.some((option) => (option as ListOption)?.isHeading);
    if (multiple && modelValue.value && !grouped) {
      //sort selected items to the top
      const selected = filtered.filter((option) => !!option && modelValue?.value.includes(option.value));
      if (selected.length > 0) selected.push(null);
      const unselected = filtered.filter((option) => !!option && !modelValue?.value.includes(option.value));
      filtered = [...selected, ...unselected];
    }

    if (!search.value) return filtered;

    const matchesSearch = (option: DropdownOption) => option.label?.toLowerCase().includes(search.value.toLowerCase());
    filtered = grouped
      ? filterGroups(filtered as ListOption[], matchesSearch)
      : filtered.filter((filterKey) => !!filterKey && matchesSearch(filterKey as DropdownOption));

    const created = createdOption.value;
    if (created) {
      const { heading, ...option } = created;
      const headingOption = { label: heading, value: null, isHeading: true };
      // the created value goes last, under the matches, so the list reads "what exists" before "add new"
      filtered = [...filtered, ...(filtered.length ? [null] : []), ...(heading ? [headingOption] : []), option];
    }

    return filtered;
  });

  const prettyModelValue = computed(() => {
    if (!options?.value) return "";

    if (multiple && Array.isArray(modelValue.value)) {
      if (!modelValue.value?.length) return "";

      const selectedOptions = modelValue.value.map((value: any) => {
        const option = options.value.find((option) => option?.value === value);
        return option?.label || "";
      });

      if (selectedOptions.length > 2) {
        return `${selectedOptions.slice(0, 2).join(", ")} +${selectedOptions.length - 2}`;
      }

      return selectedOptions.join(", ");
    }

    let option = null;

    if (valueIsArray.value && modelValue.value.length)
      option = options.value.find((option) => option?.value === modelValue.value[0]);
    else option = options.value.find((option) => option?.value === modelValue.value);
    return option?.label || "";
  });

  const setFocusOnItem = (_index: number, direction: "down" | "up") => {
    const items = optionsWrapper.value?.querySelectorAll(".dropdown__item");
    if (!items) return;

    const itemsArray = Array.from(items) as HTMLElement[];
    const currentIndex = itemsArray.indexOf(document.activeElement as HTMLElement);

    let nextIndex: number;
    if (direction === "down") {
      nextIndex = currentIndex === -1 || currentIndex === itemsArray.length - 1 ? 0 : currentIndex + 1;
    } else {
      nextIndex = currentIndex === -1 || currentIndex === 0 ? itemsArray.length - 1 : currentIndex - 1;
    }

    itemsArray[nextIndex]?.focus();
  };

  watch(open, () => {
    if (open.value) {
      //focus search input
      setTimeout(() => {
        searchElm.value?.focus({ preventScroll: true });
      }, 150);
    } else {
      search.value = "";
    }
  });

  const getOptionFromValue = (value: any) => {
    if (!filteredOptions.value) return null;
    return filteredOptions.value.find((option) => option?.value === value);
  };

  const selectOption = (value: string) => {
    const option = getOptionFromValue(value);
    if (option?.disabled) return;
    if (!multiple) toggleDropdown();
    nextTick(() => {
      if (option && option.handler) {
        option.handler();
        return;
      }
      if (!emit) return;

      if (multiple) {
        // combobox: clear the search and keep typing focus so the next value can be entered straight away
        if (creatable) {
          search.value = "";
          searchElm.value?.focus({ preventScroll: true });
        }
        const newValue = modelValue.value
          ? modelValue.value.includes(value)
            ? modelValue.value.filter((v: any) => v !== value)
            : [...modelValue.value, value]
          : [value];
        emit("select", newValue);
        emit("update:modelValue", newValue);
        modelValue.value = newValue;
        return;
      }

      emit("select", value);
      if (valueIsArray.value) value = [value];
      emit("update:modelValue", value);
      modelValue.value = value;
    });
  };

  const valueIsArray = computed(() => Array.isArray(modelValue.value));

  return {
    searchElm,
    optionsWrapper,
    search,
    filteredOptions,
    prettyModelValue,
    modelValue,
    setFocusOnItem,
    selectOption,
    selectCreatedOption,
  };
};
